import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Grid,
  Stack,
  CircularProgress,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Radio,
  IconButton,
  Tooltip,
  Chip,
  Collapse
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CustomModal from '../CustomModal';
import CustomSnackbar from '../Snackbar';
import { updateUser, listGroups, updateUserGroups } from '../../services/adminService';
import {
  getAccountLicenses,
  getUserAssetsByEmail,
  removeUserLicense,
  updateAssetExpiration,
  addUserLicenseForUser
} from '../../services';

const STEPS = ['User Info', 'Account Licenses'];

const EditUserModal = ({ open, onClose, onUserUpdated, user, isAdmin }) => {
  const theme = useTheme();

  const focusSx = {
    '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#04aeea' } },
    '& .MuiInputLabel-root.Mui-focused': { color: '#04aeea' },
  };

  const headerSx = {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50]
  };

  // Stepper
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({ email: '', name: '', phoneNumber: '' });
  const [errors, setErrors] = useState({ email: '', phoneNumber: '' });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Group membership
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // Assigned licenses
  const [userAssets, setUserAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [pendingExpirations, setPendingExpirations] = useState({});
  const [savedExpirations, setSavedExpirations] = useState({});
  const [savingExpiration, setSavingExpiration] = useState({});
  const [removingLicense, setRemovingLicense] = useState({});
  const [licenseError, setLicenseError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Add license inline panel
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [availableLicenses, setAvailableLicenses] = useState([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [selectedNewLicenseID, setSelectedNewLicenseID] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState('2099-12-31');
  const [addingLicense, setAddingLicense] = useState(false);
  const [addLicenseError, setAddLicenseError] = useState('');

  useEffect(() => {
    if (open && user) {
      setActiveStep(0);
      setFormData({
        email: user.email || '',
        name: user.name || '',
        phoneNumber: user.phoneNumber || ''
      });
      setErrors({ email: '', phoneNumber: '' });
      setApiError('');
      setLoading(false);
      setLicenseError('');
      setShowAddLicense(false);
      setSelectedNewLicenseID(null);
      setNewExpirationDate('2099-12-31');
      setSelectedGroups(user.groups || []);
      if (isAdmin) {
        setGroupsLoading(true);
        listGroups()
          .then(groups => setAvailableGroups(groups || []))
          .catch(e => console.error('[EditUserModal] listGroups error:', e))
          .finally(() => setGroupsLoading(false));
      }

      setAssetsLoading(true);
      getUserAssetsByEmail(user.email)
        .then((result) => {
          const assets = result?.data ?? [];
          setUserAssets(assets);
          const exps = {};
          assets.forEach((a) => {
            exps[a.pkAccountLicenseID] = a.ExpirationDate
              ? new Date(a.ExpirationDate).toISOString().split('T')[0]
              : '2099-12-31';
          });
          setPendingExpirations(exps);
        })
        .catch((e) => console.error('[EditUserModal] getUserAssetsByEmail error:', e))
        .finally(() => setAssetsLoading(false));

      setLicensesLoading(true);
      getAccountLicenses()
        .then((result) => {
          const active = (result?.data ?? []).filter((l) => l.IsActive?.data?.[0] === 1);
          setAvailableLicenses(active);
        })
        .catch((e) => console.error('[EditUserModal] getAccountLicenses error:', e))
        .finally(() => setLicensesLoading(false));

    } else if (!open) {
      setActiveStep(0);
      setFormData({ email: '', name: '', phoneNumber: '' });
      setErrors({ email: '', phoneNumber: '' });
      setApiError('');
      setUserAssets([]);
      setPendingExpirations({});
      setSavedExpirations({});
      setSavingExpiration({});
      setRemovingLicense({});
      setLicenseError('');
      setShowAddLicense(false);
      setAvailableLicenses([]);
      setSelectedNewLicenseID(null);
      setNewExpirationDate('2099-12-31');
      setAddLicenseError('');
      setLoading(false);
      setAvailableGroups([]);
      setSelectedGroups([]);
      setGroupsLoading(false);
    }
  }, [open, user]);

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    if (!phoneRegex.test(phoneNumber)) return 'Phone must be in E.164 format (e.g., +1234567890)';
    return '';
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setApiError('');
    let error = '';
    if (field === 'email') error = validateEmail(value);
    else if (field === 'phoneNumber') error = validatePhoneNumber(value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const isFormValid = () => {
    return !validateEmail(formData.email) && !validatePhoneNumber(formData.phoneNumber);
  };

  const isFormDirty = user && (
    formData.email !== (user.email || '') ||
    formData.name !== (user.name || '') ||
    formData.phoneNumber !== (user.phoneNumber || '')
  );

  const isGroupsDirty = user && (() => {
    const orig = [...(user.groups || [])].sort().join(',');
    const curr = [...selectedGroups].sort().join(',');
    return orig !== curr;
  })();

  // Step 1 — save profile/groups then advance, or just advance if unchanged
  const handleNext = async () => {
    if (!isFormDirty && !isGroupsDirty) {
      setActiveStep(1);
      return;
    }

    if (!user?.username) {
      setApiError('No user selected for editing.');
      return;
    }

    const emailError = validateEmail(formData.email);
    const phoneError = validatePhoneNumber(formData.phoneNumber);
    setErrors({ email: emailError, phoneNumber: phoneError });
    if (emailError || phoneError) return;

    setLoading(true);
    setApiError('');

    try {
      if (isFormDirty) {
        const result = await updateUser(
          user.username,
          formData.email,
          formData.name || null,
          formData.phoneNumber || null
        );
        if (!result.success) {
          setApiError(result.message || 'Failed to update user');
          setLoading(false);
          return;
        }
      }

      if (isAdmin && isGroupsDirty) {
        const result = await updateUserGroups(user.username, selectedGroups);
        if (!result.success) {
          setApiError(result.message || 'Failed to update group membership');
          setLoading(false);
          return;
        }
      }

      setLoading(false);
      setActiveStep(1);
    } catch (error) {
      console.error('Error updating user:', error);
      setApiError(error.message || 'Failed to update user. Please try again.');
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && isFormValid() && !loading && activeStep === 0) handleNext();
  };

  const handleSaveExpiration = async (asset) => {
    const id = asset.pkAccountLicenseID;
    setSavingExpiration((prev) => ({ ...prev, [id]: true }));
    setLicenseError('');
    try {
      const result = await updateAssetExpiration(asset.fkUserID, id, pendingExpirations[id]);
      if (!result?.success) {
        setSnackbar({ open: true, message: result?.error || 'Failed to update expiration', severity: 'error' });
      } else {
        setSavedExpirations((prev) => ({ ...prev, [id]: pendingExpirations[id] }));
        setSnackbar({ open: true, message: 'Expiration date updated', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to update expiration', severity: 'error' });
    } finally {
      setSavingExpiration((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleRemoveLicense = async (asset) => {
    setRemovingLicense((prev) => ({ ...prev, [asset.pkAccountLicenseID]: true }));
    setLicenseError('');
    try {
      const result = await removeUserLicense(asset.fkUserID, asset.pkAccountLicenseID);
      if (result?.success) {
        setUserAssets((prev) => prev.filter((a) => a.pkAccountLicenseID !== asset.pkAccountLicenseID));
      } else {
        setLicenseError(result?.error || 'Failed to remove license');
      }
    } catch (e) {
      setLicenseError(e.response?.data?.error || e.message || 'Failed to remove license');
    } finally {
      setRemovingLicense((prev) => ({ ...prev, [asset.pkAccountLicenseID]: false }));
    }
  };

  const handleAddLicense = async () => {
    if (!selectedNewLicenseID) return;
    setAddingLicense(true);
    setAddLicenseError('');
    try {
      const result = await addUserLicenseForUser(formData.email, selectedNewLicenseID, newExpirationDate);
      if (result?.success) {
        const refreshed = await getUserAssetsByEmail(formData.email);
        const assets = refreshed?.data ?? [];
        setUserAssets(assets);
        const exps = {};
        assets.forEach((a) => {
          exps[a.pkAccountLicenseID] = a.ExpirationDate
            ? new Date(a.ExpirationDate).toISOString().split('T')[0]
            : '2099-12-31';
        });
        setPendingExpirations(exps);
        setShowAddLicense(false);
        setSelectedNewLicenseID(null);
        setNewExpirationDate('2099-12-31');
      } else {
        setAddLicenseError(result?.error || 'Failed to assign license');
      }
    } catch (e) {
      setAddLicenseError(e.message || 'Failed to assign license');
    } finally {
      setAddingLicense(false);
    }
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const sectionHeaderSx = {
    px: 2.5,
    py: 1,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(4,174,234,0.08)'
      : 'rgba(4,174,234,0.04)',
    borderBottom: `1px solid ${theme.palette.divider}`
  };

  const sectionLabelSx = {
    color: '#04aeea',
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontSize: '0.68rem'
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Edit User"
      subtitle={activeStep === 0 ? 'Update user account details' : 'Manage account licenses for this user'}
      icon={<EditIcon sx={{ color: 'white', fontSize: 24 }} />}
      maxWidth="md"
      contentPadding={true}
      disableBackdropClick={true}
      actions={
        <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 3, py: 2, width: '100%' }}>
          {activeStep === 1 && (
            <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
              License changes are saved immediately
            </Typography>
          )}
          {activeStep === 0 && <Box sx={{ flex: 1 }} />}

          {activeStep === 1 && (
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              disabled={loading || addingLicense}
              sx={{
                borderColor: theme.palette.grey[300],
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.grey[400],
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[50]
                }
              }}
            >
              ← Back
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={onClose}
            disabled={loading || addingLicense}
            sx={{
              borderColor: theme.palette.grey[300],
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.grey[400],
                backgroundColor: theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[50]
              }
            }}
          >
            Cancel
          </Button>

          {activeStep === 0 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isFormValid() || loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                backgroundColor: '#04aeea',
                color: 'white',
                '&:hover': { backgroundColor: '#0398ce' },
                '&:disabled': {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.grey[500]
                }
              }}
            >
              {loading ? 'Saving...' : (isFormDirty || isGroupsDirty) ? 'Save & Next' : 'Next →'}
            </Button>
          )}

          {activeStep === 1 && (
            <Button
              variant="contained"
              onClick={onUserUpdated}
              disabled={addingLicense}
              sx={{
                backgroundColor: '#04aeea',
                color: 'white',
                '&:hover': { backgroundColor: '#0398ce' },
                '&:disabled': {
                  backgroundColor: theme.palette.grey[300],
                  color: theme.palette.grey[500]
                }
              }}
            >
              Done
            </Button>
          )}
        </Stack>
      }
    >
      <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Stepper header */}
        <Stepper activeStep={activeStep} sx={{
          mb: 1,
          '& .MuiStepIcon-root.Mui-active': { color: '#04aeea' },
          '& .MuiStepIcon-root.Mui-completed': { color: '#04aeea' },
        }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: User Info */}
        {activeStep === 0 && (
          <>
            {apiError && (
              <Alert severity="error" onClose={() => setApiError('')}>
                {apiError}
              </Alert>
            )}

            {/* ── Profile Card ── */}
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="caption" sx={sectionLabelSx}>
                  Profile Information
                </Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2.5}>
                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      required
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      onKeyPress={handleKeyPress}
                      error={!!errors.email}
                      helperText={errors.email || 'Primary email for the account'}
                      disabled={loading}
                      autoComplete="email"
                      sx={focusSx}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={handleChange('name')}
                      onKeyPress={handleKeyPress}
                      helperText="Optional"
                      disabled={loading}
                      autoComplete="name"
                      sx={focusSx}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }} >
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={formData.phoneNumber}
                      onChange={handleChange('phoneNumber')}
                      onKeyPress={handleKeyPress}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber || 'Optional — Format: +1234567890'}
                      disabled={loading}
                      autoComplete="tel"
                      placeholder="+1234567890"
                      sx={focusSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* ── Group Membership Card (ADMINS only) ── */}
            {isAdmin && <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="caption" sx={sectionLabelSx}>
                  Group Membership
                </Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {groupsLoading ? (
                  <CircularProgress size={20} sx={{ color: '#04aeea' }} />
                ) : availableGroups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No groups available</Typography>
                ) : (
                  <FormGroup row>
                    {availableGroups.map((group) => (
                      <FormControlLabel
                        key={group}
                        control={
                          <Checkbox
                            checked={selectedGroups.includes(group)}
                            onChange={(e) => {
                              setSelectedGroups(prev =>
                                e.target.checked
                                  ? [...prev, group]
                                  : prev.filter(g => g !== group)
                              );
                            }}
                            disabled={loading}
                            sx={{ color: '#04aeea', '&.Mui-checked': { color: '#04aeea' } }}
                          />
                        }
                        label={group}
                      />
                    ))}
                  </FormGroup>
                )}
              </Box>
            </Box>}
          </>
        )}

        {/* Step 2: Account Licenses */}
        {activeStep === 1 && (
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>

            {/* License card header bar */}
            <Box sx={{
              ...sectionHeaderSx,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Account Licenses
                </Typography>
                {!assetsLoading && (
                  <Chip
                    label={userAssets.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      backgroundColor: userAssets.length > 0 ? '#04aeea' : theme.palette.grey[400],
                      color: 'white'
                    }}
                  />
                )}
              </Stack>
              <Button
                size="small"
                onClick={() => { setShowAddLicense((v) => !v); setAddLicenseError(''); }}
                startIcon={showAddLicense
                  ? <CloseIcon fontSize="small" />
                  : <AddIcon fontSize="small" />}
                sx={{
                  color: showAddLicense ? theme.palette.text.secondary : '#04aeea',
                  fontSize: '0.8rem',
                  '&:hover': { backgroundColor: 'transparent' }
                }}
              >
                {showAddLicense ? 'Close' : 'Add License'}
              </Button>
            </Box>

            {/* License error */}
            {licenseError && (
              <Box sx={{ px: 2.5, pt: 2 }}>
                <Alert severity="error" onClose={() => setLicenseError('')}>
                  {licenseError}
                </Alert>
              </Box>
            )}

            {/* Add License Collapse panel */}
            <Collapse in={showAddLicense}>
              <Box sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(4,174,234,0.06)'
                  : 'rgba(4,174,234,0.03)',
                p: 2.5
              }}>
                <Typography variant="caption" sx={{ ...sectionLabelSx, display: 'block', mb: 1.5 }}>
                  Select a License to Add
                </Typography>

                {addLicenseError && (
                  <Alert severity="error" onClose={() => setAddLicenseError('')} sx={{ mb: 1.5 }}>
                    {addLicenseError}
                  </Alert>
                )}

                {licensesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ color: '#04aeea' }} />
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 220, overflow: 'auto', mb: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow sx={headerSx}>
                            <TableCell padding="checkbox" />
                            <TableCell sx={{ fontWeight: 600 }}>Account Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {availableLicenses
                            .filter((l) => !userAssets.some((a) => a.pkAccountLicenseID === l.pkAccountLicenseID))
                            .map((lic) => (
                              <TableRow
                                key={lic.pkAccountLicenseID}
                                hover
                                selected={selectedNewLicenseID === lic.pkAccountLicenseID}
                                onClick={() => setSelectedNewLicenseID(lic.pkAccountLicenseID)}
                                sx={{
                                  cursor: 'pointer',
                                  '&.Mui-selected': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                      ? 'rgba(4,174,234,0.15)'
                                      : 'rgba(4,174,234,0.08)'
                                  }
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <Radio
                                    checked={selectedNewLicenseID === lic.pkAccountLicenseID}
                                    size="small"
                                    sx={{ color: '#04aeea', '&.Mui-checked': { color: '#04aeea' } }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: selectedNewLicenseID === lic.pkAccountLicenseID ? 600 : 400 }}
                                  >
                                    {lic.AccountName}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {lic.MetaData}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                  <TextField
                    label="Expiration Date"
                    type="date"
                    value={newExpirationDate}
                    onChange={(e) => setNewExpirationDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ width: 200, ...focusSx }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddLicense}
                    disabled={!selectedNewLicenseID || addingLicense}
                    startIcon={addingLicense ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{ backgroundColor: '#04aeea', '&:hover': { backgroundColor: '#0398ce' } }}
                  >
                    {addingLicense ? 'Assigning...' : 'Assign License'}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowAddLicense(false)}
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            </Collapse>

            {/* Assigned licenses */}
            {assetsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#04aeea' }} />
              </Box>
            ) : userAssets.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[100],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AddIcon sx={{ color: theme.palette.text.disabled, fontSize: 22 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">No licenses assigned</Typography>
                <Typography variant="caption" color="text.disabled">
                  Use &ldquo;Add License&rdquo; above to assign one
                </Typography>
              </Box>
            ) : (
              <Box sx={{ px: 2.5, py: 1.5 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={headerSx}>
                        <TableCell sx={{ fontWeight: 600 }}>Asset Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expiration Date</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userAssets.map((asset) => {
                        const originalDate = savedExpirations[asset.pkAccountLicenseID]
                          ?? (asset.ExpirationDate
                            ? new Date(asset.ExpirationDate).toISOString().split('T')[0]
                            : '2099-12-31');
                        const currentDate = pendingExpirations[asset.pkAccountLicenseID] ?? originalDate;
                        const isDirty = currentDate !== originalDate;
                        return (
                          <TableRow
                            key={asset.pkAccountLicenseID}
                            sx={{ '&:last-child td': { border: 0 } }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {asset.AccountName}
                              </Typography>
                              {asset.MetaData && (
                                <Typography variant="caption" color="text.secondary">
                                  {asset.MetaData}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <TextField
                                  type="date"
                                  value={currentDate}
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 150, ...focusSx }}
                                  onChange={(e) =>
                                    setPendingExpirations((prev) => ({
                                      ...prev,
                                      [asset.pkAccountLicenseID]: e.target.value
                                    }))
                                  }
                                />
                                {isDirty && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSaveExpiration(asset)}
                                    disabled={!!savingExpiration[asset.pkAccountLicenseID]}
                                    sx={{
                                      borderColor: '#04aeea',
                                      color: '#04aeea',
                                      minWidth: 60,
                                      '&:hover': {
                                        borderColor: '#0398ce',
                                        backgroundColor: 'rgba(4,174,234,0.04)'
                                      }
                                    }}
                                  >
                                    {savingExpiration[asset.pkAccountLicenseID]
                                      ? <CircularProgress size={14} color="inherit" />
                                      : 'Save'}
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5 }}>
                              <Tooltip title="Remove license">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveLicense(asset)}
                                  disabled={!!removingLicense[asset.pkAccountLicenseID]}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  {removingLicense[asset.pkAccountLicenseID]
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

          </Box>
        )}

      </Box>

      <CustomSnackbar
        severity={snackbar.severity}
        message={snackbar.message}
        open={snackbar.open}
        handleClose={handleSnackbarClose}
      />
    </CustomModal>
  );
};

export default EditUserModal;
